import type { ReactNode } from "react";
import type { DataTableColumn } from "../../ui/DataTable/types";
import TableContainer from "../../ui/DataTable/TableContainer";
import TableHeader from "../../ui/DataTable/TableHeader";
import TableRowSkeleton from "../../ui/DataTable/TableRowSkeleton";
import AdminTablePagination from "./AdminTablePagination";

type AdminTableShellProps = {
  columns: DataTableColumn[];
  filters: ReactNode;
  rows: ReactNode;
  mobileCards?: ReactNode;
  isLoading: boolean;
  isEmpty: boolean;
  emptyMessage: string;
  page: number;
  pageSize: number;
  resultsCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (nextPage: number) => void;
  skeletonRowCount?: number;
};

const AdminTableShell = ({
  columns,
  filters,
  rows,
  mobileCards,
  isLoading,
  isEmpty,
  emptyMessage,
  page,
  pageSize,
  resultsCount,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
  skeletonRowCount = 5,
}: AdminTableShellProps) => {
  return (
    <section className="mt-2">
      {filters}

      <div className="grid gap-4 lg:hidden">
        {isLoading
          ? Array.from({ length: skeletonRowCount }).map((_, index) => (
              <div key={index} className="h-40 animate-pulse rounded-[1.5rem] border border-myteengig-border bg-white shadow-sm" />
            ))
          : null}

        {!isLoading ? mobileCards : null}

        {isEmpty ? (
          <div className="rounded-[1.5rem] border border-dashed border-myteengig-border bg-white px-4 py-6 text-sm text-myteengig-text-muted">
            {emptyMessage}
          </div>
        ) : null}
      </div>

      <TableContainer className="hidden lg:block">
        <table className="min-w-full divide-y divide-myteengig-border">
          <TableHeader columns={columns} />
          <tbody className="divide-y divide-myteengig-border">
            {isLoading
              ? Array.from({ length: skeletonRowCount }).map((_, index) => (
                  <TableRowSkeleton key={index} columnCount={columns.length} />
                ))
              : null}

            {!isLoading ? rows : null}

            {isEmpty ? (
              <tr>
                <td className="px-4 py-6 text-sm text-myteengig-text-muted" colSpan={columns.length}>
                  {emptyMessage}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </TableContainer>

      <AdminTablePagination
        page={page}
        pageSize={pageSize}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        resultsCount={resultsCount}
        onPageChange={onPageChange}
      />
    </section>
  );
};

export default AdminTableShell;
